/**
 * InfragraphParser
 *
 * Parses infrastructure topology data in InfraGraph format.
 * This parser handles:
 * - Device definitions with components (choice-based typing)
 * - Infrastructure instances (with count field)
 * - Edges with schemes (one2one, many2many, ring)
 * - Slice notation for bulk connectivity
 * - Link definitions with physical properties
 *
 * Uses existing expansion utilities (InstanceExpander, EdgeExpander) for processing.
 */

import { InfragraphInfrastructure, ExpandedInstance, ExpandedComponent, ExpandedEdge, Link, Device } from '../types/infragraph';
import { VisualizationNode, VisualizationLink, ZoomLevel } from '../types/visualization';
import { InstanceExpander, calculateLayoutCoordinates } from './instanceExpander';
import { EdgeExpander } from './edgeExpander';

// Coordinate reference constants
const REFERENCE_WIDTH = 2000;
const REFERENCE_HEIGHT = 2000;
const CENTER_X = REFERENCE_WIDTH / 2;   // = 1000
const CENTER_Y = REFERENCE_HEIGHT / 2;  // = 1000

/**
 * Transform user coordinates to SVG coordinates
 */
function transformToSVG(userX: number | undefined, userY: number | undefined): { x: number; y: number } {
  const x = userX !== undefined ? (CENTER_X + userX) : 0;
  const y = userY !== undefined ? (CENTER_Y - userY) : 0;
  return { x, y };
}

/**
 * Calculate SVG coordinates from layout, with fallback to grid position
 * Reusable for both instances and components
 */
function calculateSVGPosition(
  layout: { x?: number; y?: number; fixed?: boolean } | undefined,
  index: number,
  gridSpacing: { x: number; y: number }
): { x: number; y: number; fx: number | undefined; fy: number | undefined } {
  const svgCoords = transformToSVG(layout?.x, layout?.y);
  const x = layout?.x !== undefined ? svgCoords.x : (index % 20) * gridSpacing.x;
  const y = layout?.y !== undefined ? svgCoords.y : Math.floor(index / 20) * gridSpacing.y;
  const fixed = layout?.fixed ?? false;

  return {
    x,
    y,
    fx: fixed ? x : undefined,
    fy: fixed ? y : undefined
  };
}

export class InfragraphParser {
  private infrastructure: InfragraphInfrastructure | null = null;
  private instanceExpander: InstanceExpander;
  private edgeExpander: EdgeExpander;
  private expandedInstances: ExpandedInstance[] = [];
  private expandedComponents: ExpandedComponent[] = [];
  private expandedEdges: ExpandedEdge[] = [];

  constructor() {
    this.instanceExpander = new InstanceExpander();
    this.edgeExpander = new EdgeExpander();
  }

  /**
   * Parse infragraph infrastructure data
   * @param data - Infrastructure data in official infragraph format
   * @throws Error if data is invalid or missing required fields
   */
  parse(data: InfragraphInfrastructure): void {
    // Validate required fields
    if (!data) {
      throw new Error('Infrastructure data is required');
    }

    if (!data.name) {
      throw new Error('Infrastructure name is required');
    }

    if (!data.devices) {
      throw new Error('Infrastructure must have devices array');
    }

    // Links can be at top level or inside devices, so it's optional at top level
    // if (!data.links) {
    //   throw new Error('Infrastructure must have links array');
    // }

    if (!data.instances) {
      throw new Error('Infrastructure must have instances array');
    }

    if (!data.edges) {
      throw new Error('Infrastructure must have edges array');
    }

    // Store the infrastructure data
    this.infrastructure = data;

    // Expand instances
    this.expandedInstances = this.instanceExpander.expandAllInstances(data.instances);

    // Expand components for each instance
    this.expandedComponents = this._expandAllComponents();

    // Expand edges using slice notation and schemes
    this.expandedEdges = this._expandAllEdges();

    // Validate references
    this._validateReferences();
  }

  /**
   * Get the parsed infrastructure data
   * @returns The parsed infrastructure
   * @throws Error if parse() has not been called yet
   */
  getInfrastructure(): InfragraphInfrastructure {
    if (!this.infrastructure) {
      throw new Error('No infrastructure data. Call parse() first.');
    }

    return this.infrastructure;
  }

  /**
   * Get expanded instances
   * @returns Array of expanded instances
   */
  getExpandedInstances(): ExpandedInstance[] {
    return this.expandedInstances;
  }

  /**
   * Get expanded components
   * @returns Array of expanded components
   */
  getExpandedComponents(): ExpandedComponent[] {
    return this.expandedComponents;
  }

  /**
   * Get expanded edges
   * @returns Array of expanded edges
   */
  getExpandedEdges(): ExpandedEdge[] {
    return this.expandedEdges;
  }

  /**
   * Reset the parser state
   */
  reset(): void {
    this.infrastructure = null;
    this.expandedInstances = [];
    this.expandedComponents = [];
    this.expandedEdges = [];
  }

  /**
   * Expand all components for all instances
   * Handles device-in-device composition recursively
   * @private
   */
  private _expandAllComponents(): ExpandedComponent[] {
    if (!this.infrastructure) return [];

    const components: ExpandedComponent[] = [];

    for (const instance of this.expandedInstances) {
      const device = this.infrastructure.devices.find(d => d.name === instance.device);
      if (device) {
        const instanceComponents = this._expandDeviceComponentsRecursive(
          device,
          instance.fullName
        );
        components.push(...instanceComponents);
      }
    }

    return components;
  }

  /**
   * Recursively expand device components, handling device-in-device composition
   * @private
   */
  private _expandDeviceComponentsRecursive(
    device: Device,
    instancePrefix: string
  ): ExpandedComponent[] {
    const components: ExpandedComponent[] = [];

    for (const component of device.components) {
      // Create component nodes for this level
      for (let i = 0; i < component.count; i++) {
        const componentFullName = `${instancePrefix}.${component.name}.${i}`;

        // Calculate layout coordinates for this component index (same as instance expansion)
        const userCoords = calculateLayoutCoordinates(component.layout, i, component.count);
        let componentLayout = component.layout;

        if (userCoords.x !== undefined && userCoords.y !== undefined) {
          // Store calculated coordinates in expanded component
          componentLayout = {
            x: userCoords.x,
            y: userCoords.y,
            fixed: component.layout?.fixed ?? true
          };
        }

        components.push({
          fullName: componentFullName,
          instanceName: instancePrefix,
          componentBaseName: component.name,
          index: i,
          component: component,
          layout: componentLayout
        });

        // If this is a device-in-device composition, recursively expand its internal components
        if (component.choice === 'device' && component.name) {
          const composedDevice = this.infrastructure!.devices.find(d => d.name === component.name);
          if (composedDevice) {
            const nestedComponents = this._expandDeviceComponentsRecursive(
              composedDevice,
              componentFullName
            );
            components.push(...nestedComponents);
          }
        }
      }
    }

    return components;
  }

  /**
   * Expand all edges using slice notation and schemes
   * Includes both infrastructure-level edges and device-level edges
   * @private
   */
  private _expandAllEdges(): ExpandedEdge[] {
    if (!this.infrastructure) return [];

    const edges: ExpandedEdge[] = [];

    // Expand infrastructure-level edges (connections between devices)
    for (const edge of this.infrastructure.edges) {
      const expandedEdges = this.edgeExpander.expandInfrastructureEdge(edge);
      edges.push(...expandedEdges);
    }

    // Expand device-level edges (internal component connections)
    for (const instance of this.expandedInstances) {
      const device = this.infrastructure.devices.find(d => d.name === instance.device);
      if (device && device.edges) {
        const deviceEdges = this._expandDeviceEdgesRecursive(device, instance.fullName);
        edges.push(...deviceEdges);
      }
    }

    return edges;
  }

  /**
   * Recursively expand device-level edges including device-in-device edges
   * @param device - The device definition
   * @param instancePrefix - The instance path prefix (e.g., "board.0")
   * @private
   */
  private _expandDeviceEdgesRecursive(device: Device, instancePrefix: string): ExpandedEdge[] {
    const edges: ExpandedEdge[] = [];

    // Expand edges defined in this device
    if (device.edges) {
      for (const deviceEdge of device.edges) {
        const expandedDeviceEdges = this.edgeExpander.expandDeviceEdge(
          deviceEdge,
          instancePrefix
        );
        edges.push(...expandedDeviceEdges);
      }
    }

    // Recursively expand edges for device-in-device components
    if (device.components) {
      for (const component of device.components) {
        if (component.choice === 'device' && component.name) {
          const composedDevice = this.infrastructure!.devices.find(d => d.name === component.name);
          if (composedDevice) {
            // Expand each instance of the composed device component
            const count = component.count || 1;
            for (let i = 0; i < count; i++) {
              const nestedPrefix = `${instancePrefix}.${component.name}.${i}`;
              const nestedEdges = this._expandDeviceEdgesRecursive(composedDevice, nestedPrefix);
              edges.push(...nestedEdges);
            }
          }
        }
      }
    }

    return edges;
  }

  /**
   * Find a link by name, searching both top-level and device-level links
   * If deviceNames are provided, only searches those specific devices first (for scoped lookups)
   *
   * @param linkName - Name of the link to find
   * @param deviceNames - Optional array of device names to scope the search
   * @private
   */
  private _findLink(linkName: string, deviceNames?: string[]): Link | undefined {
    // If device names provided, search those specific devices first
    if (deviceNames && deviceNames.length > 0) {
      for (const deviceName of deviceNames) {
        const device = this.infrastructure!.devices.find(d => d.name === deviceName);
        if (device?.links) {
          const deviceLink = device.links.find(l => l.name === linkName);
          if (deviceLink) return deviceLink;
        }
      }
    }

    // Check top-level links
    if (this.infrastructure!.links) {
      const topLevelLink = this.infrastructure!.links.find(l => l.name === linkName);
      if (topLevelLink) return topLevelLink;
    }

    // If not found and no device scope provided, search all device-level links
    // (for validation purposes or fallback)
    if (!deviceNames || deviceNames.length === 0) {
      for (const device of this.infrastructure!.devices) {
        if (device.links) {
          const deviceLink = device.links.find(l => l.name === linkName);
          if (deviceLink) return deviceLink;
        }
      }
    }

    return undefined;
  }

  /**
   * Validate all references (devices, links, components)
   * @private
   * @throws Error if any references are invalid
   */
  private _validateReferences(): void {
    if (!this.infrastructure) return;

    // Validate device references in instances
    for (const instance of this.infrastructure.instances) {
      const device = this.infrastructure.devices.find(d => d.name === instance.device);
      if (!device) {
        throw new Error(`Invalid device reference in instance "${instance.name}": device "${instance.device}" not found`);
      }
    }

    // Validate link references in edges
    // Links must be defined at top-level OR in the devices participating in the edge
    // This matches the scoped lookup used in generateVisualizationLinks
    for (const edge of this.infrastructure.edges) {
      // Get device names from edge endpoints (same logic as generateVisualizationLinks)
      const ep1InstanceBase = edge.ep1.instance.split('[')[0];
      const ep2InstanceBase = edge.ep2.instance.split('[')[0];

      const ep1Instance = this.infrastructure.instances.find(i => i.name === ep1InstanceBase);
      const ep2Instance = this.infrastructure.instances.find(i => i.name === ep2InstanceBase);

      const deviceScope: string[] = [];
      if (ep1Instance) deviceScope.push(ep1Instance.device);
      if (ep2Instance) deviceScope.push(ep2Instance.device);

      // Use device-scoped lookup to ensure link is accessible to this edge
      const link = this._findLink(edge.link, deviceScope);
      if (!link) {
        const scopeDesc = deviceScope.length > 0
          ? `in top-level links or in devices [${deviceScope.join(', ')}]`
          : 'in top-level or device-level links';
        throw new Error(`Invalid link reference in edge: link "${edge.link}" not found ${scopeDesc}`);
      }
    }

    // Validate component references in edges
    for (const edge of this.infrastructure.edges) {
      // Validate ep1 component
      const ep1InstanceBase = edge.ep1.instance.split('[')[0];
      const ep1Instance = this.infrastructure.instances.find(i => i.name === ep1InstanceBase);
      if (ep1Instance) {
        const ep1Device = this.infrastructure.devices.find(d => d.name === ep1Instance.device);
        if (ep1Device) {
          const ep1ComponentBase = edge.ep1.component.split('[')[0];
          const ep1Component = ep1Device.components.find(c => c.name === ep1ComponentBase);
          if (!ep1Component) {
            throw new Error(`Invalid component reference in edge ep1: component "${ep1ComponentBase}" not found in device "${ep1Device.name}"`);
          }
        }
      }

      // Validate ep2 component
      const ep2InstanceBase = edge.ep2.instance.split('[')[0];
      const ep2Instance = this.infrastructure.instances.find(i => i.name === ep2InstanceBase);
      if (ep2Instance) {
        const ep2Device = this.infrastructure.devices.find(d => d.name === ep2Instance.device);
        if (ep2Device) {
          const ep2ComponentBase = edge.ep2.component.split('[')[0];
          const ep2Component = ep2Device.components.find(c => c.name === ep2ComponentBase);
          if (!ep2Component) {
            throw new Error(`Invalid component reference in edge ep2: component "${ep2ComponentBase}" not found in device "${ep2Device.name}"`);
          }
        }
      }
    }
  }

  /**
   * Infer network location from connections for instances that don't have it explicitly set
   * Looks at infrastructure edges to find connected devices that have network defined
   */
  private _inferNetworksFromConnections(): void {
    if (!this.infrastructure?.edges) return;

    // Early exit: check if any instance needs network inference
    const needsInference = this.expandedInstances.some(inst => !inst.location?.network);
    if (!needsInference) {
      return; // All instances already have network defined
    }

    // Build a map of instance baseName -> connected instance baseNames
    const connectionMap = new Map<string, Set<string>>();

    this.infrastructure.edges.forEach(edge => {
      const ep1Base = edge.ep1.instance.split('[')[0];
      const ep2Base = edge.ep2.instance.split('[')[0];

      if (!connectionMap.has(ep1Base)) connectionMap.set(ep1Base, new Set());
      if (!connectionMap.has(ep2Base)) connectionMap.set(ep2Base, new Set());

      connectionMap.get(ep1Base)!.add(ep2Base);
      connectionMap.get(ep2Base)!.add(ep1Base);
    });

    // For each instance without network, try to infer from connections
    this.expandedInstances.forEach(instance => {
      // Skip if network is already explicitly set
      if (instance.location?.network) return;

      // Find connected instances using baseName
      const connectedInstances = connectionMap.get(instance.baseName);
      if (!connectedInstances) return;

      // Look for a connected instance that has network defined
      const connectedArray = Array.from(connectedInstances);
      for (const connectedBaseName of connectedArray) {
        const connectedInstance = this.expandedInstances.find(i => i.baseName === connectedBaseName);
        if (connectedInstance?.location?.network) {
          // Infer network from this connection
          if (!instance.location) {
            instance.location = { network: connectedInstance.location.network };
          } else {
            instance.location.network = connectedInstance.location.network;
          }
          console.log(`Inferred network "${connectedInstance.location.network}" for instance "${instance.baseName}" from connection to "${connectedBaseName}"`);
          break; // Use the first match
        }
      }
    });
  }

  /**
   * Generate visualization nodes for hierarchical rendering
   * Creates nodes for: site → pod → network → rack → device → component
   */
  generateVisualizationNodes(): VisualizationNode[] {
    if (!this.infrastructure) return [];

    // Infer networks from connections before generating nodes
    this._inferNetworksFromConnections();

    const nodes: VisualizationNode[] = [];
    const siteMap = new Map<string, Set<string>>();
    const podMap = new Map<string, Set<string>>();
    const networkMap = new Map<string, Set<string>>();
    const rackMap = new Map<string, Set<string>>();

    // Ensure all instances have complete location hierarchy
    this.expandedInstances.forEach(instance => {
      const location = instance.location || {
        site: 'Site-1',
        pod: 'Pod-1',
        network: 'Network-1'
      };

      // Fill in missing hierarchy levels with defaults
      const site = location.site || 'Site-1';
      const pod = location.pod || 'Pod-1';
      const network = location.network || 'Network-1';
      const rack = location.rack; // Optional

      const siteKey = site;
      const podKey = `${siteKey}/${pod}`;
      const networkKey = `${podKey}/${network}`;

      if (!siteMap.has(siteKey)) siteMap.set(siteKey, new Set());
      if (!podMap.has(podKey)) podMap.set(podKey, new Set());
      if (!networkMap.has(networkKey)) networkMap.set(networkKey, new Set());

      siteMap.get(siteKey)!.add(podKey);
      podMap.get(podKey)!.add(networkKey);

      // Only create rack level if rack is specified
      if (rack) {
        const rackKey = `${networkKey}/${rack}`;
        if (!rackMap.has(rackKey)) rackMap.set(rackKey, new Set());
        networkMap.get(networkKey)!.add(rackKey);
        rackMap.get(rackKey)!.add(instance.fullName);
      } else {
        // Device is directly under network
        networkMap.get(networkKey)!.add(instance.fullName);
      }
    });

    // Generate site nodes
    Array.from(siteMap.keys()).forEach((siteKey, index) => {
      nodes.push({
        id: siteKey,
        type: 'site',
        name: siteKey,
        x: index * 200,
        y: 0,
        children: Array.from(siteMap.get(siteKey)!)
      });
    });

    // Generate pod nodes
    Array.from(podMap.keys()).forEach((podKey, index) => {
      const [siteKey] = podKey.split('/');
      nodes.push({
        id: podKey,
        type: 'pod',
        name: podKey.split('/').pop() || podKey,
        x: (index % 5) * 150,
        y: Math.floor(index / 5) * 150,
        parent: siteKey,
        children: Array.from(podMap.get(podKey)!)
      });
    });

    // Generate network nodes
    Array.from(networkMap.keys()).forEach((networkKey, index) => {
      const podKey = networkKey.substring(0, networkKey.lastIndexOf('/'));
      nodes.push({
        id: networkKey,
        type: 'network',
        name: networkKey.split('/').pop() || networkKey,
        x: (index % 8) * 120,
        y: Math.floor(index / 8) * 120,
        parent: podKey,
        children: Array.from(networkMap.get(networkKey)!)
      });
    });

    // Generate rack nodes for navigation hierarchy (but they won't be rendered)
    if (rackMap.size > 0) {
      Array.from(rackMap.keys()).forEach((rackKey, index) => {
        const networkKey = rackKey.substring(0, rackKey.lastIndexOf('/'));
        nodes.push({
          id: rackKey,
          type: 'rack',
          name: rackKey.split('/').pop() || rackKey,
          x: (index % 10) * 100,
          y: Math.floor(index / 10) * 100,
          parent: networkKey,
          children: Array.from(rackMap.get(rackKey)!)
        });
      });
    }

    // Generate device nodes from expanded instances
    this.expandedInstances.forEach((instance, index) => {
      const location = instance.location || {
        site: 'Site-1',
        pod: 'Pod-1',
        network: 'Network-1'
      };

      const site = location.site || 'Site-1';
      const pod = location.pod || 'Pod-1';
      const network = location.network || 'Network-1';
      const rack = location.rack;

      // Determine parent - either rack or network
      // Rack nodes exist in hierarchy for navigation but won't be rendered
      const networkKey = `${site}/${pod}/${network}`;
      const parentKey = rack ? `${networkKey}/${rack}` : networkKey;

      // Find ALL component children for this device (including nested components for device-in-device)
      // A component belongs to this device if its fullName starts with the instance fullName
      const componentChildren = this.expandedComponents
        .filter(comp => comp.fullName.startsWith(instance.fullName + '.'))
        .map(comp => comp.fullName);

      // Calculate SVG position using shared helper function
      // After instance expansion, layout should have x and y coordinates
      // (instanceExpander calculates them from coordinates list or spacing mode)
      const position = calculateSVGPosition(instance.layout, index, { x: 50, y: 50 });

      nodes.push({
        id: instance.fullName,
        type: 'device',
        name: instance.fullName,
        x: position.x,
        y: position.y,
        fx: position.fx,
        fy: position.fy,
        parent: parentKey,
        children: componentChildren.length > 0 ? componentChildren : undefined,
        metadata: {
          deviceType: instance.device,
          location: instance.location
        }
      });
    });

    // Generate component nodes
    this.expandedComponents.forEach((component, index) => {
      // Calculate SVG position using shared helper function (same as instances)
      // Pre-calculated layout coordinates from component expansion
      const position = calculateSVGPosition(component.layout, index, { x: 30, y: 30 });

      nodes.push({
        id: component.fullName,
        type: 'component',
        name: component.componentBaseName,
        x: position.x,
        y: position.y,
        fx: position.fx,
        fy: position.fy,
        parent: component.instanceName,
        metadata: {
          componentType: component.component.choice
        }
      });
    });

    return nodes;
  }

  /**
   * Generate visualization links from expanded edges
   */
  generateVisualizationLinks(): VisualizationLink[] {
    if (!this.infrastructure) return [];

    const links: VisualizationLink[] = [];
    let linkIndex = 0;

    // Process all expanded edges
    this.expandedEdges.forEach((edge) => {
      // Determine device scope for link lookup
      // Extract instance names from source/target to find their device types
      const sourceInstance = edge.source.split('.')[0]; // e.g., "server-1-1[0]" from "server-1-1[0].gpu-0"
      const targetInstance = edge.target.split('.')[0];

      const getDeviceName = (instanceName: string): string | undefined => {
        const baseInstance = instanceName.split('[')[0];
        const instance = this.infrastructure!.instances.find(i => i.name === baseInstance);
        return instance?.device;
      };

      const sourceDevice = getDeviceName(sourceInstance);
      const targetDevice = getDeviceName(targetInstance);
      const deviceScope = [sourceDevice, targetDevice].filter((d): d is string => d !== undefined);

      // Find link definition for metadata, scoped to the devices involved in this edge
      const linkDef = this._findLink(edge.link, deviceScope);

      // Helper to extract bandwidth value from choice-based structure
      const getBandwidth = (bw: any) => {
        if (!bw) return undefined;
        const unit = bw.choice;
        const value = bw[unit];
        if (value === undefined) return undefined;

        // Convert unit to display format
        const unitMap: Record<string, string> = {
          'gigabits_per_second': 'Gbps',
          'gigabytes_per_second': 'GB/s',
          'gigatransfers_per_second': 'GT/s'
        };
        return { value, unit: unitMap[unit] || unit };
      };

      // Helper to extract latency value from choice-based structure
      const getLatency = (lat: any) => {
        if (!lat) return undefined;
        const unit = lat.choice;
        const value = lat[unit];
        if (value === undefined) return undefined;
        return { value, unit };
      };

      const link: VisualizationLink = {
        id: `link-${linkIndex++}`,
        source: edge.source,
        target: edge.target,
        bandwidth: getBandwidth(linkDef?.physical?.bandwidth),
        latency: getLatency(linkDef?.physical?.latency),
        protocol: linkDef?.name,
        metadata: {
          linkName: edge.link,
          linkType: edge.link, // Include link type for filtering (nvlink, pcie, infiniband, etc.)
          scheme: edge.scheme,
          level: (edge.source.includes('.') && edge.target.includes('.') ? 'component' : 'device') as 'component' | 'device'
        }
      };

      links.push(link);
    });

    return links;
  }

  /**
   * Define zoom levels for hierarchical navigation
   */
  getZoomLevels(): ZoomLevel[] {
    return [
      {
        level: 0,
        name: 'Global View',
        nodeTypes: ['site'],
        minZoom: 0.1,
        maxZoom: 0.5
      },
      {
        level: 1,
        name: 'Site View',
        nodeTypes: ['site', 'pod'],
        minZoom: 0.4,
        maxZoom: 1.0
      },
      {
        level: 2,
        name: 'Pod View',
        nodeTypes: ['pod', 'network'],
        minZoom: 0.8,
        maxZoom: 2.0
      },
      {
        level: 3,
        name: 'Network View',
        nodeTypes: ['network', 'rack', 'device'],
        minZoom: 1.2,
        maxZoom: 3.0
      },
      {
        level: 4,
        name: 'Rack View',
        nodeTypes: ['rack', 'device'],
        minZoom: 2.0,
        maxZoom: 5.0
      },
      {
        level: 5,
        name: 'Device View',
        nodeTypes: ['device', 'component'],
        minZoom: 4.0,
        maxZoom: 10.0
      }
    ];
  }

  /**
   * Get the initial navigation context (auto-navigate to first interesting level)
   */
  getInitialContext(): string | null {
    if (!this.infrastructure) return null;

    // Generate nodes to analyze the hierarchy
    const nodes = this.generateVisualizationNodes();

    // Define hierarchy order from top to bottom
    const hierarchyLevels = ['site', 'pod', 'network', 'rack', 'device', 'component'];

    let currentContext: string | null = null; // Start at global view

    // Helper function to get nodes at level for context
    const getNodesAtLevel = (levelType: string, contextId: string | null) => {
      return nodes.filter(node => {
        if (node.type !== levelType) return false;
        // If no current context, only show top-level nodes (sites)
        if (!contextId) return levelType === 'site';
        // Otherwise, show direct children of current context
        return node.parent === contextId;
      });
    };

    // Simple logic: if current layer has only one node, drill down into it and repeat
    for (const level of hierarchyLevels) {
      const nodesAtLevel = getNodesAtLevel(level, currentContext);

      if (nodesAtLevel.length > 1) {
        // Multiple nodes at this level - stop here
        return currentContext;
      } else if (nodesAtLevel.length === 1) {
        const singleNode = nodesAtLevel[0];

        // Count all child nodes under this single node
        const allChildNodes = nodes.filter(node => node.parent === singleNode.id);

        if (allChildNodes.length > 1) {
          // This single node has multiple children - drill down to it to show them
          return singleNode.id;
        }

        // Single node with 0 or 1 child - drill down into it
        currentContext = singleNode.id;
      } else {
        // No nodes at this level - stop
        return currentContext;
      }
    }

    return currentContext;
  }

  /**
   * Get statistics about the infrastructure
   */
  getStatistics() {
    if (!this.infrastructure) return null;

    return {
      totalDevices: this.expandedInstances.length,
      totalConnections: this.expandedEdges.length,
      deviceTypes: new Set(this.infrastructure.devices.map(d => d.name)).size,
      sites: new Set(this.expandedInstances.map(i => i.location?.site).filter(Boolean)).size,
      pods: new Set(this.expandedInstances.map(i =>
        i.location ? `${i.location.site}/${i.location.pod}` : null
      ).filter(Boolean)).size,
      racks: new Set(this.expandedInstances.map(i =>
        i.location ? `${i.location.site}/${i.location.pod}/${i.location.rack}` : null
      ).filter(Boolean)).size
    };
  }
}
